import React, {useState} from 'react';
import {Box, IconButton, Typography, Snackbar, Alert, Container} from '@mui/material';
import {ContentCopy, OpenInNew} from '@mui/icons-material';
import pattern from '../assets/pattern.svg'

interface ProjectWithPreviewProps {
    children: React.ReactNode;
    projectUrl: string;
}

export const ProjectWithPreview: React.FC<ProjectWithPreviewProps> = ({children, projectUrl}) => {
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const handleCopyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(projectUrl);
            setSnackbarOpen(true);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleOpenInNewTab = () => {
        window.open(projectUrl, '_blank', 'noopener,noreferrer');
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    return (
        <>
            <Box
                sx={{
                    height: '100dvh',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 3,
                    position: 'relative',
                    padding: 1,
                    '@media (max-width: 900px)': {
                        flexDirection: 'column',
                    },
                }}
            >
                {/* Pattern layer */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundImage: `url(${pattern})`,
                        backgroundRepeat: 'repeat',
                        zIndex: 0,
                        pointerEvents: 'none',
                    }}
                />

                {/* Project Description */}
                <Container
                    className="custom-scrollbar"
                    sx={{
                        flex: '1 1 50%',
                        zIndex: 1,
                        position: 'relative',
                        maxHeight: "100dvh",
                        overflow: "auto"
                    }}
                >
                    {children}
                </Container>

                {/* Project Preview */}
                <Box
                    sx={{
                        flex: '1 1 50%',
                        zIndex: 1,
                        position: 'relative',
                        backgroundColor: 'rgba(255, 255, 255, 0.01)',
                        backdropFilter: 'blur(16px) saturate(180%)',
                        border: '2px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: '100%',
                        '@media (max-width: 900px)': {
                            minHeight: '50%',
                        },
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            border: '2px solid rgba(6, 156, 63, 0.4)',
                            boxShadow:
                                '0 12px 40px rgba(6, 156, 63, 0.2),' +
                                '0 4px 12px rgba(0, 0, 0, 0.3),' +
                                'inset 0 1px 0 rgba(255, 255, 255, 0.05)',
                        },
                    }}
                >
                    {/* Address Bar */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            padding: '12px 16px',
                            backgroundColor: 'rgba(6, 156, 63, 0.08)',
                            borderBottom: '1px solid rgba(6, 156, 63, 0.2)',
                            backdropFilter: 'blur(8px)',
                        }}
                    >
                        <IconButton
                            size="small"
                            onClick={handleCopyToClipboard}
                            sx={{
                                color: '#069c3f',
                                '&:hover': {
                                    backgroundColor: 'rgba(6, 156, 63, 0.15)',
                                },
                            }}
                            title="Copy to clipboard"
                        >
                            <ContentCopy fontSize="small"/>
                        </IconButton>

                        <IconButton
                            size="small"
                            onClick={handleOpenInNewTab}
                            sx={{
                                color: '#069c3f',
                                '&:hover': {
                                    backgroundColor: 'rgba(6, 156, 63, 0.15)',
                                },
                            }}
                            title="Open in new tab"
                        >
                            <OpenInNew fontSize="small"/>
                        </IconButton>

                        <Typography
                            variant="body2"
                            sx={{
                                color: '#d1d1d1',
                                fontSize: '0.875rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1,
                            }}
                        >
                            {projectUrl}
                        </Typography>
                    </Box>

                    {/* Iframe Container */}
                    <Box
                        sx={{
                            flex: 1,
                            position: 'relative',
                            backgroundColor: '#ffffff',
                            overflow: 'hidden',
                        }}
                    >
                        <iframe
                            src={projectUrl}
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none',
                                display: 'block',
                            }}
                            title="Project Preview"
                        />
                    </Box>
                </Box>
            </Box>

            {/* Snackbar for copy confirmation */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity="success"
                    sx={{
                        backgroundColor: 'rgba(6, 156, 63, 0.9)',
                        color: '#ffffff',
                    }}
                >
                    URL copied to clipboard!
                </Alert>
            </Snackbar>
        </>
    );
};